<template>
  <div class="h-full flex flex-col bg-[#f8fafc] gap-6 overflow-hidden">
    <div class="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden border-t-4 border-t-[#0d4169] flex flex-col">
      <div class="px-6 pt-5 pb-1 flex justify-between items-center shrink-0">
        <h2 class="text-base font-bold text-[#0d4169] text-left">Debone Percentage</h2>
      </div>

      <div class="px-4 sm:px-6 pb-5 pt-2 shrink-0">
        <div class="flex flex-wrap gap-x-3 sm:gap-x-10 gap-y-3 w-full">
          <!-- Department (frontend filter only) -->
          <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">Department</label>
            <select
              v-model="filters.departmentId"
              class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]"
              @change="handleDepartmentFilterChange"
            >
              <option value="">All Departments</option>
              <option v-for="dep in activeDepartments" :key="dep.id" :value="String(dep.id)">
                {{ dep.name }}
              </option>
            </select>
          </div>

          <!-- Item Name (unique from table list, frontend filter only) -->
          <div class="flex flex-col gap-1 w-full min-w-0 sm:w-[200px]">
            <label class="text-[0.7rem] font-bold text-slate-600 text-left">Item Name</label>
            <select
              v-model="filters.itemId"
              class="w-full px-2 py-1.5 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs bg-white cursor-pointer h-[34px]"
              @change="handleItemFilterChange"
            >
              <option value="">All Items</option>
              <option v-for="item in uniqueTableItems" :key="item.id" :value="String(item.id)">
                {{ item.itemName }}
              </option>
            </select>
          </div>
        </div>
      </div>

      <div class="px-6 pb-6 overflow-auto flex-1 custom-scrollbar">
        <div v-if="loading" class="flex items-center justify-center py-16">
          <div class="h-8 w-8 border-4 border-gray-300 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <table v-else class="w-full deboni-table">
          <thead>
            <tr>
              <th class="w-10 text-center sticky-col bg-[#f1f5f9]">No.</th>
              <th class="min-w-[140px] sticky-col left-[40px] bg-[#f1f5f9]">Department</th>
              <th class="min-w-[150px] sticky-col left-[180px] bg-[#f1f5f9]">Deboni Item Name</th>
              <th class="min-w-[200px]">Deboni Product Name</th>
              <th class="w-24 text-center">U.O.M</th>
              <!-- <th class="w-24 text-center">Quantity</th> -->
              <th class="min-w-[140px] text-center">
                <div class="flex items-center justify-center gap-2">
                  <span>%</span>
                  <template v-if="batchEditMode">
                    <a
                      @click="saveBatchPercentages"
                      class="text-green-600 text-xs font-bold cursor-pointer hover:underline"
                      :class="batchSaving ? 'opacity-50 pointer-events-none' : ''"
                    >{{ batchSaving ? 'Saving...' : 'Save' }}</a>
                    <a
                      @click="cancelBatchEdit"
                      class="text-slate-500 text-xs font-bold cursor-pointer hover:underline"
                      :class="batchSaving ? 'opacity-50 pointer-events-none' : ''"
                    >Cancel</a>
                  </template>
                  <a
                    v-else
                    @click="startBatchEdit"
                    class="text-blue-500 text-xs font-bold cursor-pointer hover:underline"
                    :class="!canEditDebonePercentage || editingId !== null || !filteredRegistrations.length ? 'opacity-50 pointer-events-none' : ''"
                  >Edit</a>
                </div>
              </th>
              <th class="w-28 text-center">Unit Price</th>
              <th class="w-36 text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(row, index) in filteredRegistrations"
              :key="row.id"
              class="hover:bg-slate-50 transition-colors group"
              :class="[
                row.status !== 1 ? 'opacity-60' : '',
                isLastRowOfItemGroup(index) ? 'item-group-end' : ''
              ]"
            >
              <td class="text-center sticky-col bg-white group-hover:bg-slate-50">{{ index + 1 }}</td>
              <td class="sticky-col left-[40px] bg-white group-hover:bg-slate-50">{{ row.departmentName || '-' }}</td>
              <td class="sticky-col left-[180px] bg-white group-hover:bg-slate-50">{{ row.itemName }}</td>
              <td class="text-slate-700">{{ row.productName }}</td>
              <td class="text-center text-slate-600">{{ getUomLabel(row.uom) }}</td>
              <!-- <td class="text-center text-slate-600">{{ isPiecesUomValue(row.uom) ? (row.quantity ?? '-') : '-' }}</td> -->
              <td class="text-center font-bold text-blue-600">
                <template v-if="batchEditMode">
                  <div class="relative inline-flex items-center w-[72px]">
                    <input
                      v-model.number="batchPercentages[row.id]"
                      type="number"
                      class="w-full px-1.5 py-1 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs text-center pr-5"
                      placeholder="0"
                      :disabled="batchSaving"
                    >
                    <span class="absolute right-1 text-[0.65rem] text-slate-400 font-bold">%</span>
                  </div>
                </template>
                <template v-else-if="editingId === row.id">
                  <div class="relative inline-flex items-center w-[72px]">
                    <input
                      v-model.number="editPercentage"
                      type="number"
                      class="w-full px-1.5 py-1 border border-[#cbd5e1] rounded outline-none focus:border-[#0d4169] text-xs text-center pr-5"
                      placeholder="0"
                    >
                    <span class="absolute right-1 text-[0.65rem] text-slate-400 font-bold">%</span>
                  </div>
                </template>
                <template v-else>{{ row.percentage }}%</template>
              </td>
              <td class="text-center text-slate-700">{{ formatPrice(row.unitPrice) }}</td>
              <td class="text-center">
                <div class="flex items-center justify-center gap-3">
                  <template v-if="editingId === row.id">
                    <a
                      @click="savePercentage(row)"
                      class="text-green-600 text-xs font-bold cursor-pointer hover:underline"
                      :class="saving ? 'opacity-50 pointer-events-none' : ''"
                    >{{ saving ? 'Saving...' : 'Save' }}</a>
                    <a
                      @click="cancelEdit"
                      class="text-slate-500 text-xs font-bold cursor-pointer hover:underline"
                    >Cancel</a>
                  </template>
                  <template v-else>
                    <a
                      @click="handleEdit(row)"
                      class="text-blue-500 text-xs font-bold cursor-pointer hover:underline"
                      :class="!canEditDebonePercentage || batchEditMode ? 'opacity-50 pointer-events-none' : ''"
                    >Edit</a>
                  </template>
                  <label
                    class="toggle-switch"
                    :class="!canEditDebonePercentage || row.__togglingStatus ? 'opacity-50 pointer-events-none' : ''"
                    title="Toggle active status"
                  >
                    <input
                      type="checkbox"
                      :checked="row.status === 1"
                      :disabled="!canEditDebonePercentage || !!row.__togglingStatus"
                      @change="toggleRowStatus(row)"
                    >
                    <span class="slider"></span>
                  </label>
                </div>
              </td>
            </tr>
            <tr v-if="!loading && filteredRegistrations.length === 0">
              <td colspan="9" class="text-center py-10 text-slate-400 italic">No deboni registrations found.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Toast (page-local only) -->
    <Transition name="toast">
      <div v-if="toast.show" class="fixed top-4 right-4 z-[1100] max-w-sm pointer-events-none">
        <div
          :class="[
            'px-6 py-3 rounded-lg shadow-2xl text-white flex items-center gap-3 min-w-[280px] pointer-events-auto',
            toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
          ]"
        >
          <span v-if="toast.type === 'success'">✅</span>
          <span v-else>❌</span>
          <span class="font-medium text-sm">{{ toast.message }}</span>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import store from '../../store'
import { apiFetch } from '../../utils/api'

const LEGACY_UOM_TO_LABEL = { Kg: 'K.G.', Ltr: 'liter', Pc: 'Pieces' }

const getUomLabel = (value) => LEGACY_UOM_TO_LABEL[value] || value || '-'

const isPiecesUomValue = (uom) => {
  const label = getUomLabel(uom)
  return label === 'Pieces' || uom === 'Pieces' || uom === 'Pc' || uom === 'Piece'
}

function formatPrice(value) {
  return Number(value || 0).toFixed(2)
}

function percentagesEqual(a, b) {
  const left = Number(a)
  const right = Number(b)
  if (!Number.isFinite(left) || !Number.isFinite(right)) return false
  return Math.round(left * 100) / 100 === Math.round(right * 100) / 100
}

const departments = ref([])
const items = ref([])
const loading = ref(false)
const saving = ref(false)
const batchSaving = ref(false)
const toast = ref({ show: false, message: '', type: 'success' })
let toastTimer = null

function showToast(message, type = 'success') {
  if (toastTimer) clearTimeout(toastTimer)
  toast.value = { show: true, message, type }
  toastTimer = setTimeout(() => {
    toast.value.show = false
  }, 3500)
}

const filters = ref({
  departmentId: '',
  itemId: '',
})

const editingId = ref(null)
const editPercentage = ref('')
const batchEditMode = ref(false)
const batchPercentages = ref({})

const currentUser = computed(() => store.getters.currentUser)
const companyId = computed(() => currentUser.value?.companyId || currentUser.value?.company_id || null)
const branchId = computed(() => currentUser.value?.branchId || currentUser.value?.branch_id || null)

const userPrivileges = computed(() => {
  if (!currentUser.value?.privilege) return {}
  return typeof currentUser.value.privilege === 'string'
    ? JSON.parse(currentUser.value.privilege)
    : currentUser.value.privilege
})
const hasAdminPrivilege = computed(() => userPrivileges.value?.All === true || userPrivileges.value?.all === true)
const debonePercentagePrivilege = computed(() =>
  userPrivileges.value?.['Debone Percentage'] || userPrivileges.value?.['debone percentage'] || null
)
/** Edit / on-off requires All or Debone Percentage Edit */
const canEditDebonePercentage = computed(() => hasAdminPrivilege.value || !!debonePercentagePrivilege.value?.Edit)

const activeDepartments = computed(() =>
  departments.value.filter((d) => {
    if ((d.status ?? 1) !== 1) return false
    const isCentral = d.isCentral ?? d.is_central
    return isCentral === 0 || isCentral === false
  }).sort((a, b) => a.name.localeCompare(b.name))
)

/** Unique Deboni Item Name options from the registrations table (optionally by department). */
const uniqueTableItems = computed(() => {
  let rows = items.value
  const depId = String(filters.value.departmentId || '').trim()
  if (depId) {
    rows = rows.filter((row) => String(row.departmentId ?? '') === depId)
  }

  const map = new Map()
  for (const row of rows) {
    if (row.itemId == null || row.itemId === '') continue
    const id = String(row.itemId)
    if (map.has(id)) continue
    map.set(id, {
      id: row.itemId,
      itemName: row.itemName || '-',
    })
  }

  return [...map.values()].sort((a, b) =>
    String(a.itemName || '').localeCompare(String(b.itemName || ''), undefined, { sensitivity: 'base' })
  )
})

/** Frontend-only filtering by department and/or item. */
const filteredRegistrations = computed(() => {
  const activeDepartmentIds = new Set(activeDepartments.value.map((dep) => String(dep.id)))
  let result = [...items.value].filter((row) => {
    const departmentId = String(row.departmentId ?? '').trim()
    return departmentId && activeDepartmentIds.has(departmentId)
  })

  const depId = String(filters.value.departmentId || '').trim()
  if (depId) {
    result = result.filter((row) => String(row.departmentId ?? '') === depId)
  }

  const itemId = String(filters.value.itemId || '').trim()
  if (itemId) {
    result = result.filter((row) => String(row.itemId ?? '') === itemId)
  }

  return result.sort((a, b) =>
    String(a.itemName || '').localeCompare(String(b.itemName || ''), undefined, { sensitivity: 'base' })
    || String(a.productName || a.deboniName || '').localeCompare(String(b.productName || b.deboniName || ''), undefined, { sensitivity: 'base' })
  )
})

/** True when this row is the last of its Deboni Item Name group (before a different item). */
function isLastRowOfItemGroup(index) {
  const rows = filteredRegistrations.value
  const current = rows[index]
  const next = rows[index + 1]
  if (!current || !next) return false
  const currentKey = String(current.itemId ?? current.itemName ?? '')
  const nextKey = String(next.itemId ?? next.itemName ?? '')
  return currentKey !== nextKey
}

function handleDepartmentFilterChange() {
  const allowed = new Set(uniqueTableItems.value.map((item) => String(item.id)))
  if (filters.value.itemId && !allowed.has(String(filters.value.itemId))) {
    filters.value.itemId = ''
  }
  if (batchEditMode.value) cancelBatchEdit()
  if (editingId.value !== null) cancelEdit()
}

function handleItemFilterChange() {
  if (batchEditMode.value) cancelBatchEdit()
  if (editingId.value !== null) cancelEdit()
}

async function loadDepartments() {
  if (!companyId.value) return
  let endpoint = `/departments/company/${companyId.value}`
  if (branchId.value) {
    endpoint = `/departments/branch/${branchId.value}`
  }
  const response = await apiFetch(endpoint, { method: 'GET' })
  if (!response.ok) return
  const data = await response.json().catch(() => [])
  departments.value = Array.isArray(data) ? data : []
}

async function loadRegistrations() {
  loading.value = true
  try {
    const response = await apiFetch('/debones/registrations', { method: 'GET' })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to load deboni registrations')
    }
    const data = await response.json()
    items.value = Array.isArray(data) ? data : []
  } catch (err) {
    items.value = []
  } finally {
    loading.value = false
  }
}

function getActivePercentageTotalForItem(itemId, excludeRegistrationId = null, draftById = null, departmentId = null) {
  const parentItemId = Number(itemId)
  const parentDepartmentId = Number(departmentId)
  if (!Number.isFinite(parentItemId) || !Number.isFinite(parentDepartmentId)) return 0

  return items.value
    .filter((row) => {
      if (Number(row.itemId) !== parentItemId) return false
      // Cap 100% per department + item (not across all departments)
      if (Number(row.departmentId) !== parentDepartmentId) return false
      if ((row.status ?? 1) !== 1) return false
      if (excludeRegistrationId != null && row.id === excludeRegistrationId) return false
      return true
    })
    .reduce((sum, row) => {
      if (draftById && Object.prototype.hasOwnProperty.call(draftById, row.id)) {
        return sum + Number(draftById[row.id] || 0)
      }
      return sum + Number(row.percentage || 0)
    }, 0)
}

function validatePercentageUpdate(row) {
  const newPct = Number(editPercentage.value)
  if (!Number.isFinite(newPct)) return 'Percentage must be a valid number.'
  if (newPct < 0) return 'Percentage cannot be negative.'
  if (newPct > 100) return 'Percentage cannot exceed 100.'

  const existingTotal = getActivePercentageTotalForItem(row.itemId, row.id, null, row.departmentId)
  const combinedTotal = Math.round((existingTotal + newPct) * 100) / 100

  if (combinedTotal > 100) {
    const remaining = Math.max(0, Math.round((100 - existingTotal) * 100) / 100)
    const itemLabel = row.itemName || 'this item'
    return `Total percentage for "${itemLabel}" cannot exceed 100%. Existing active products total ${existingTotal}% — you can add up to ${remaining}%.`
  }

  return ''
}

function validateBatchPercentageUpdates(changedRows, draftById) {
  for (const row of changedRows) {
    const newPct = Number(draftById[row.id])
    if (!Number.isFinite(newPct)) return `Percentage must be a valid number for "${row.productName || row.itemName}".`
    if (newPct < 0) return `Percentage cannot be negative for "${row.productName || row.itemName}".`
    if (newPct > 100) return `Percentage cannot exceed 100 for "${row.productName || row.itemName}".`
  }

  const affectedKeys = [
    ...new Set(
      changedRows
        .map((row) => {
          const departmentId = Number(row.departmentId)
          const itemId = Number(row.itemId)
          if (!Number.isFinite(departmentId) || !Number.isFinite(itemId)) return null
          return `${departmentId}|${itemId}`
        })
        .filter(Boolean)
    ),
  ]
  for (const key of affectedKeys) {
    const [departmentId, itemId] = key.split('|').map(Number)
    const projected = Math.round(
      getActivePercentageTotalForItem(itemId, null, draftById, departmentId) * 100
    ) / 100
    if (projected > 100) {
      const itemLabel =
        items.value.find(
          (row) => Number(row.itemId) === itemId && Number(row.departmentId) === departmentId
        )?.itemName || 'this item'
      return `Total percentage for "${itemLabel}" cannot exceed 100% (projected ${projected}%).`
    }
  }

  return ''
}

function handleEdit(row) {
  if (!canEditDebonePercentage.value || batchEditMode.value) return
  editingId.value = row.id
  editPercentage.value = row.percentage ?? ''
}

function cancelEdit() {
  editingId.value = null
  editPercentage.value = ''
}

function startBatchEdit() {
  if (!canEditDebonePercentage.value || editingId.value !== null || !filteredRegistrations.value.length) return
  const drafts = {}
  for (const row of filteredRegistrations.value) {
    drafts[row.id] = row.percentage ?? ''
  }
  batchPercentages.value = drafts
  batchEditMode.value = true
}

function cancelBatchEdit() {
  batchEditMode.value = false
  batchPercentages.value = {}
}

function getChangedBatchRows() {
  return filteredRegistrations.value.filter((row) => {
    if (!Object.prototype.hasOwnProperty.call(batchPercentages.value, row.id)) return false
    return !percentagesEqual(batchPercentages.value[row.id], row.percentage)
  })
}

async function savePercentage(row) {
  if (!canEditDebonePercentage.value) return
  const validationError = validatePercentageUpdate(row)
  if (validationError) {
    showToast(validationError, 'error')
    return
  }

  saving.value = true
  try {
    const uom = getUomLabel(row.uom)
    const payload = {
      departmentId: row.departmentId ? Number(row.departmentId) : null,
      itemId: row.itemId ? Number(row.itemId) : null,
      deboniName: row.productName || row.deboniName || '',
      uom: uom === '-' ? row.uom : uom,
      quantity: isPiecesUomValue(row.uom) ? row.quantity : null,
      percentage: Number(editPercentage.value),
    }

    const response = await apiFetch(`/debones/registrations/${row.id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to update percentage')
    }

    cancelEdit()
    await loadRegistrations()
    showToast('Percentage updated successfully.', 'success')
  } catch (err) {
    showToast(err.message || 'Failed to update percentage', 'error')
  } finally {
    saving.value = false
  }
}

async function saveBatchPercentages() {
  if (!canEditDebonePercentage.value) return
  const changedRows = getChangedBatchRows()
  if (!changedRows.length) {
    showToast('No percentage changes to save.', 'error')
    return
  }

  const draftById = { ...batchPercentages.value }
  const validationError = validateBatchPercentageUpdates(changedRows, draftById)
  if (validationError) {
    showToast(validationError, 'error')
    return
  }

  batchSaving.value = true
  try {
    const updates = changedRows.map((row) => ({
      id: row.id,
      percentage: Number(batchPercentages.value[row.id]),
    }))

    const response = await apiFetch('/debones/registrations/batch-percentages', {
      method: 'PUT',
      body: JSON.stringify({ updates }),
    })
    if (!response.ok) {
      const data = await response.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to batch update percentages')
    }

    cancelBatchEdit()
    await loadRegistrations()
    showToast(
      updates.length === 1
        ? 'Percentage updated successfully.'
        : `${updates.length} percentages updated successfully.`,
      'success'
    )
  } catch (err) {
    showToast(err.message || 'Failed to batch update percentages', 'error')
  } finally {
    batchSaving.value = false
  }
}

async function toggleRowStatus(row) {
  if (!canEditDebonePercentage.value || !row?.id || row.__togglingStatus) return
  row.__togglingStatus = true
  const next = row.status === 1 ? 0 : 1
  try {
    const res = await apiFetch(`/debones/registrations/${row.id}/toggle-status`, {
      method: 'PATCH',
      body: JSON.stringify({ status: next }),
    })
    if (!res.ok) {
      const data = await res.json().catch(() => ({}))
      throw new Error(data.message || 'Failed to toggle status')
    }
    const updated = await res.json()
    const idx = items.value.findIndex((r) => r.id === row.id)
    if (idx !== -1) {
      items.value[idx] = { ...items.value[idx], ...updated, __togglingStatus: false }
    }
  } catch (e) {
    alert(e.message || 'Failed to toggle status')
  } finally {
    row.__togglingStatus = false
  }
}

onMounted(async () => {
  await Promise.all([loadDepartments(), loadRegistrations()])
})

onBeforeUnmount(() => {
  if (toastTimer) clearTimeout(toastTimer)
})
</script>

<style scoped>
table {
  font-family: 'Segoe UI', sans-serif;
  font-size: 0.75rem;
  color: #1e293b;
  border-collapse: collapse;
}

th,
td {
  padding: 8px 12px;
  text-align: left;
  border-bottom: 1px solid #e2e8f0;
  white-space: nowrap;
}

/* Separate Deboni Item Name groups — same color as "Debone Percentage" title */
tr.item-group-end > td {
  border-bottom: 1px solid #0d4169;
}

th.text-center,
td.text-center {
  text-align: center;
}

th {
  background: #f1f5f9;
  cursor: default;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 20;
}

.sticky-col {
  position: sticky;
  left: 0;
  z-index: 10;
  background-clip: padding-box;
}

th.sticky-col {
  z-index: 30;
}

input[type="number"]::-webkit-inner-spin-button,
input[type="number"]::-webkit-outer-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 28px;
  height: 15px;
  cursor: pointer;
}
.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}
.slider {
  position: absolute;
  inset: 0;
  background: #cbd5e1;
  border-radius: 34px;
  transition: 0.2s;
}
.slider::before {
  content: '';
  position: absolute;
  width: 10px;
  height: 10px;
  left: 2px;
  top: 2.5px;
  background: white;
  border-radius: 50%;
  transition: 0.2s;
}
.toggle-switch input:checked + .slider {
  background: #22c55e;
}
.toggle-switch input:not(:checked) + .slider {
  background: #ef4444;
}
.toggle-switch input:checked + .slider::before {
  transform: translateX(13px);
}

.custom-scrollbar::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}
.custom-scrollbar::-webkit-scrollbar-track {
  background: #f1f5f9;
}
.custom-scrollbar::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}
.custom-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}
.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
